// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import {
  createConfiguration,
  DefaultApi,
  ServerConfiguration,
  Middleware,
  RequestContext,
  ResponseContext,
} from '@digitalasset/json-api-ts-client';
import React, { useMemo, useEffect } from 'react';
import { BaseApiMiddleware } from '../utils/BaseApiMiddleware';
import { WSSupportWithBackOffSwitch } from '../utils/WSSupport';
import { ensureNoTrailingSlash } from '../utils/urls';
import { JsonApiContext } from './JsonApiContext';
import { WSSupportContext } from './WSSupportContext';
import { useUserState } from './UserProvider';
import { useLocalStorage } from './LocalStorageContext';

class ApiMiddleware
  extends BaseApiMiddleware<RequestContext, ResponseContext>
  implements Middleware {}

interface JsonApiProps {
  jsonApiUrl: string;
}

export const JsonApiProvider = ({
  jsonApiUrl,
  children,
}: React.PropsWithChildren<JsonApiProps>) => {
  const { loggedInUser } = useUserState();
  const { localStorageState } = useLocalStorage();

  const jsonApiClient = useMemo(() => {
    if (loggedInUser) {
      const configuration = createConfiguration({
        baseServer: new ServerConfiguration(ensureNoTrailingSlash(jsonApiUrl), {}),
        promiseMiddleware: [new ApiMiddleware(loggedInUser.userAccessToken)],
      });

      return new DefaultApi(configuration);
    }
    return undefined;
  }, [jsonApiUrl, loggedInUser]);

  const wsSupport = useMemo(() => {
    const jsonApiUrlWithoutTrailingSlash = ensureNoTrailingSlash(jsonApiUrl);
    const wsUrl = jsonApiUrlWithoutTrailingSlash.replace(/^http/, 'ws');
    const extractProtocols = () => {
      if (loggedInUser?.userAccessToken) {
        return [`jwt.token.${loggedInUser.userAccessToken}`, 'daml.ws.auth'];
      }
      return [];
    };
    return new WSSupportWithBackOffSwitch(extractProtocols, wsUrl, 0);
  }, [jsonApiUrl, loggedInUser]);

  useEffect(() => {
    if (wsSupport) {
      const enabled = localStorageState.webSocketEnabled ?? true;
      wsSupport.setEnabled(enabled);
    }
  }, [wsSupport, localStorageState.webSocketEnabled]);

  return (
    <WSSupportContext.Provider value={wsSupport}>
      <JsonApiContext.Provider value={jsonApiClient}>{children}</JsonApiContext.Provider>
    </WSSupportContext.Provider>
  );
};
